import { type FC, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { FormBuilder } from "@/shared/components/Form";
import {
  useConnectDomainMutation,
  useCurrentPlatformQuery,
  useDisconnectDomainMutation,
  useRetryDomainSetupMutation,
} from "@/partner/entities/platforms";
import { useInstanceQuery } from "@/partner/entities/instance";
import { PLATFORM_BASE_DOMAIN } from "@/partner/shared/config/platformBaseDomain";
import { makeDomainSettingsSchema } from "../model/schemas";
import {
  extractMutationErrorDetail,
  interpolationFormat,
  resolveMutationErrorKey,
} from "@/partner/shared/lib";

const CLOUDFLARE_STEPS = ["zone", "dns", "cert", "protect", "active"] as const;

const CLOUDFLARE_STATUS_STEP: Record<string, number> = {
  ZONE_MISSING: 0,
  PENDING_DNS: 1,
  PENDING_CERT: 2,
  PROTECTING: 3,
  ACTIVE: 4,
};

// Bunny-путь: зоны у нас нет и держать её не требуется, поэтому шага
// «Зона» нет вовсе, а «Защита и кеш» сводится к включению HTTPS.
const BUNNY_STEPS = ["dns", "cert", "https", "active"] as const;

const BUNNY_STATUS_STEP: Record<string, number> = {
  PENDING_DNS: 0,
  PENDING_CERT: 1,
  PROTECTING: 2,
  ACTIVE: 3,
};

const FINAL_STATUSES = new Set(["ACTIVE", "FAILED", "ZONE_MISSING"]);

/**
 * Коды отказа бекенда и тексты под них. Ключи совпадают с кодами, тот же
 * общий хелпер, что и в остальных карточках настроек: код берётся из
 * extensions, а сообщение сервера на экран не выводится вовсе.
 */
const MUTATION_ERROR_KEYS: Record<string, string> = Object.fromEntries(
  [
    "TOKEN_MISSING_ZONE_PERMISSIONS",
    "ZONE_NOT_FOUND",
    "DNS_CONFLICT",
    "APP_HOSTING_ERROR",
    "CLOUDFLARE_ERROR",
    "VERIFY_FAILED",
    "TIMEOUT",
    "DNS_TIMEOUT",
    "CERT_TIMEOUT",
    "BUNNY_API_ERROR",
    "DOMAIN_PROVIDER_NOT_CONFIGURED",
    "INVALID_HOST",
    "DOMAIN_ALREADY_CONFIGURED",
    "DOMAIN_TAKEN",
    "DOMAIN_NOT_CONFIGURED",
  ].map((code) => [code, `domainSettings.errors.${code}`])
);

const FALLBACK_ERROR_KEY = "domainSettings.errors.UNKNOWN";

// Статусы, которые двигает минутная джоба сама: показываем «ждём», отсчёт
// времени и подсказку про автообновление.
const PENDING_STATUSES = new Set(["PENDING_DNS", "PENDING_CERT", "PROTECTING"]);
const POLL_INTERVAL_MS = 15_000;

export const DomainSettingsCard: FC = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data, refetch, startPolling, stopPolling } =
    useCurrentPlatformQuery();
  const { data: instanceData } = useInstanceQuery();
  const platform = data?.currentPlatform;
  const setup = platform?.domainSetup ?? null;
  const schema = useMemo(() => makeDomainSettingsSchema(t), [t]);
  const [copiedValue, setCopiedValue] = useState<string | null>(null);

  // Провайдер уже начатого подключения важнее провайдера инстанса:
  // Cloudflare-подключение доигрывается Cloudflare'ом и после смены
  // провайдера инстанса, и карточка обязана показывать его шаги.
  const provider =
    setup?.provider ?? instanceData?.instance.domainProvider ?? "CLOUDFLARE";
  const isBunny = provider === "BUNNY";

  const steps: readonly string[] = isBunny ? BUNNY_STEPS : CLOUDFLARE_STEPS;
  const statusStep = isBunny ? BUNNY_STATUS_STEP : CLOUDFLARE_STATUS_STEP;

  /**
   * Текст карточки: в Bunny-режиме сначала ключ из `domainSettings.bunny.*`,
   * а общий `domainSettings.*` остаётся значением по умолчанию. Так тексты
   * кнопок, ожидания и снекбаров не дублируются в сиде, а различающиеся
   * шаги и объяснения переопределяются.
   */
  const tx = useMemo(
    () =>
      (key: string, options?: Record<string, unknown>): string => {
        const root = `domainSettings.${key}`;
        if (!isBunny) return t(root, options ?? {});
        return t(`domainSettings.bunny.${key}`, {
          ...(options ?? {}),
          defaultValue: t(root, options ?? {}),
        });
      },
    [isBunny, t]
  );

  // Пока подключение идёт, статус меняет фоновая джоба — подтягиваем его сами
  useEffect(() => {
    if (setup && !FINAL_STATUSES.has(setup.status)) {
      startPolling(POLL_INTERVAL_MS);
      return () => stopPolling();
    }
    stopPolling();
    return undefined;
  }, [setup, startPolling, stopPolling]);

  const showError = (error: Parameters<typeof extractMutationErrorDetail>[0]) =>
    enqueueSnackbar(
      t(
        resolveMutationErrorKey(error, MUTATION_ERROR_KEYS, FALLBACK_ERROR_KEY),
        { code: extractMutationErrorDetail(error) }
      ),
      { variant: "error" }
    );

  const [connectDomain, { loading: connecting }] = useConnectDomainMutation({
    onCompleted: () => {
      enqueueSnackbar(t("domainSettings.snackbar.connected"), {
        variant: "success",
      });
      refetch();
    },
    onError: (error) => showError(error),
  });
  const [retryDomainSetup, { loading: retrying }] = useRetryDomainSetupMutation(
    {
      onCompleted: () => refetch(),
      onError: (error) => showError(error),
    }
  );
  const [disconnectDomain, { loading: disconnecting }] =
    useDisconnectDomainMutation({
      onCompleted: () => {
        enqueueSnackbar(t("domainSettings.snackbar.disconnected"), {
          variant: "success",
        });
        refetch();
      },
      onError: (error) => showError(error),
    });

  const busy = connecting || retrying || disconnecting;
  // Для FAILED показываем не текущий (терминальный) статус, а шаг, с
  // которого упали — иначе не понять, на чём остановилось подключение.
  const fallbackStatus = isBunny ? "PENDING_DNS" : "ZONE_MISSING";
  const activeStep = setup
    ? setup.status === "FAILED"
      ? (statusStep[setup.previousStatus ?? fallbackStatus] ?? 0)
      : (statusStep[setup.status] ?? 0)
    : 0;

  // Пустая строка в сиде — валидный «нет подсказки»: i18next с
  // returnEmptyString: false вернул бы для неё сырой ключ.
  const actionKey = setup ? `explain.${setup.status}.action` : null;
  const action = actionKey ? tx(actionKey, { defaultValue: "" }) : "";
  const showAction = Boolean(action) && !action.startsWith("domainSettings.");

  const manualRecords = setup?.manualRecords ?? [];
  const showManualRecords =
    isBunny &&
    setup?.status === "PENDING_DNS" &&
    !setup.dnsManaged &&
    manualRecords.length > 0;
  const showManagedRecord =
    isBunny && setup?.status === "PENDING_DNS" && Boolean(setup.dnsManaged);
  // «Проверить» на шаге DNS: ждать минутную джобу незачем, если партнёр уже
  // прописал запись. На следующих шагах кнопки нет: там нечего проверять
  // руками, всё делает бекенд.
  const showCheckButton =
    setup?.status === "ZONE_MISSING" ||
    (isBunny && setup?.status === "PENDING_DNS");

  const copyRecord = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedValue(value);
    } catch {
      // Буфера обмена может не быть (старый браузер, отказ в разрешении):
      // значение и так на экране, партнёр скопирует его выделением.
      enqueueSnackbar(
        t("domainSettings.errors.UNKNOWN", { code: "CLIPBOARD" }),
        {
          variant: "warning",
        }
      );
    }
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title={t("domainSettings.cardTitle")}
        action={
          setup ? (
            <Chip
              size="small"
              color={
                setup.status === "ACTIVE"
                  ? "success"
                  : setup.status === "FAILED"
                    ? "error"
                    : "default"
              }
              label={tx(`status.${setup.status}`)}
            />
          ) : null
        }
      />
      <CardContent>
        {!setup && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tx("hint")}
            </Typography>
            <FormBuilder
              formId="domain-settings-form"
              schema={schema}
              initialValues={{ host: "" }}
              onSubmit={async (values) => {
                await connectDomain({
                  variables: { input: { host: String(values.host) } },
                });
              }}
              submitButton
              resetButton={false}
              submitButtonText={t("domainSettings.connectButton")}
              disabled={busy}
              actionsAlign="end"
            />
          </>
        )}

        {setup && (
          <Stack spacing={2}>
            <Typography variant="subtitle1">{setup.host}</Typography>

            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((step, index) => (
                <Step key={step}>
                  <StepLabel
                    error={setup.status === "FAILED" && index === activeStep}
                    optional={
                      <Typography variant="caption" color="text.secondary">
                        {tx(`stepHints.${step}`)}
                      </Typography>
                    }
                  >
                    {tx(`steps.${step}`)}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  {tx(`explain.${setup.status}.title`)}
                </Typography>
                <Typography variant="body2">
                  {tx(`explain.${setup.status}.body`, {
                    host: setup.host,
                    fallback: `${platform?.slug}.${PLATFORM_BASE_DOMAIN}`,
                    // Для FAILED — имя шага, с которого упали (activeStep
                    // теперь указывает на previousStatus), а не текущий
                    // терминальный статус.
                    step: tx(`steps.${steps[activeStep]}`),
                  })}
                </Typography>
                {showAction && (
                  <Typography variant="body2" color="text.secondary">
                    {action}
                  </Typography>
                )}
                {setup.status === "FAILED" && setup.lastError && (
                  <Alert severity="error">
                    {tx(`errors.${setup.lastError}`)}
                  </Alert>
                )}
                {PENDING_STATUSES.has(setup.status) && (
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {t("domainSettings.waitingSince", {
                        date: interpolationFormat(
                          setup.statusChangedAt,
                          "short",
                          i18n.language
                        ),
                        minutes: Math.max(
                          0,
                          Math.round(
                            (Date.now() -
                              new Date(setup.statusChangedAt).getTime()) /
                              60_000
                          )
                        ),
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("domainSettings.autoRefresh")}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>

            {showManagedRecord && (
              <Alert severity="info">
                <Typography variant="subtitle2">
                  {tx("managed.title")}
                </Typography>
                <Typography variant="body2">{tx("managed.body")}</Typography>
              </Alert>
            )}

            {showManualRecords && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2">
                    {tx("manual.title")}
                  </Typography>
                  <Typography variant="body2">{tx("manual.body")}</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{tx("manual.columns.type")}</TableCell>
                        <TableCell>{tx("manual.columns.name")}</TableCell>
                        <TableCell>{tx("manual.columns.value")}</TableCell>
                        <TableCell padding="none" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {manualRecords.map((record) => (
                        <TableRow key={`${record.type}-${record.name}`}>
                          <TableCell>{record.type}</TableCell>
                          <TableCell sx={{ wordBreak: "break-all" }}>
                            {record.name}
                          </TableCell>
                          <TableCell sx={{ wordBreak: "break-all" }}>
                            {record.value}
                          </TableCell>
                          <TableCell padding="none">
                            <Tooltip
                              title={
                                copiedValue === record.value
                                  ? tx("manual.copied")
                                  : tx("manual.copy")
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() => copyRecord(record.value)}
                                aria-label={tx("manual.copy")}
                              >
                                <ContentCopyIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {manualRecords.some((record) => record.type === "ALIAS") && (
                    <Typography variant="caption" color="text.secondary">
                      {tx("manual.apexHint")}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )}

            {setup.status === "ACTIVE" && setup.activatedAt && (
              <Typography variant="body2" color="text.secondary">
                {t("domainSettings.activeSince", {
                  date: interpolationFormat(
                    setup.activatedAt,
                    "short",
                    i18n.language
                  ),
                })}
              </Typography>
            )}

            <Stack direction="row" spacing={1}>
              {(setup.status === "FAILED" || showCheckButton) && (
                <Button
                  variant="contained"
                  disabled={busy}
                  onClick={() => retryDomainSetup()}
                >
                  {t(
                    setup.status === "FAILED"
                      ? "domainSettings.retryButton"
                      : "domainSettings.checkButton"
                  )}
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                disabled={busy}
                onClick={() => {
                  if (
                    window.confirm(
                      tx("disconnectConfirm", {
                        host: setup.host,
                        fallback: `${platform?.slug}.${PLATFORM_BASE_DOMAIN}`,
                      })
                    )
                  ) {
                    disconnectDomain();
                  }
                }}
              >
                {t("domainSettings.disconnectButton")}
              </Button>
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
